import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IndexDBService } from '../../common/indexDB.service';
import { ITableData } from '../../../models/reports/ITableData';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { ReportPageOrientation } from '../../../models/reports/ReportPageOrientation';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { IDataItem } from '../../../models/reports/IDataItem';
import { CONSTANTS } from '../../../Constants/Constants';
import { IData } from '../../../models/reports/IData';
import { SQWebAreaChildren } from '../../../models/SQWebAreaChildren';
import { SQWebArea, MaterialLevelwiseData, SQWebSystem, Material, SystemChildren } from '../../../models/dtos';
import { ReportsAPIService } from '../../api/reports-api.service';
import { firstValueFrom } from 'rxjs';
import { CommonDataService } from '../../common-data.service';
import { Cartoon } from '../../../models/common/Cartoon';
import { LoadingSpinnerService } from '../../common/loading-spinner.service';

@Injectable()
export class SolGraphicsReportService implements OnDestroy {

  public cartoonsIdList: string[] = [];
  private cartoonsData: Cartoon[] = [];
  private cartoonReportData: ITableData[] = [];
  private showColourSymbol: boolean = false;
  private lastRenderedWithColourSymbol: boolean = false;
  private showIO: boolean = false;

  constructor(
    private readonly translate: TranslateService,
    private readonly commonDataService: CommonDataService,
    private readonly reportsCommonService: ReportsCommonService,
    private readonly reportsAPIService: ReportsAPIService,
    private readonly indexDBService: IndexDBService,
    private readonly loadingSpinnerService: LoadingSpinnerService
  ) { }

  async generateReport(reportId: ReportList, pageOrientation: ReportPageOrientation, internalReport: boolean = true): Promise<void> {
    try {
      const reportName = this.reportsCommonService.getSelectedReportName();
      let reportTitle = this.translate.instant(reportName);
      const reportHeaderData = this.commonDataService.getReportHeaderData();
      let reportData = {
        selectionName: reportTitle,
        reportTitle: reportTitle,
        reportTitles: {
          data: this.reportsCommonService.generateReportTitles(reportHeaderData)
        },
        tableData: await this.generateTableData(reportId),
        pageOrientation: pageOrientation,
        internalReport: internalReport,
        isReportNotAvailable: reportId === ReportList.ApplicationDetailViewModel 
      }
      // Emit the generated report data
      this.reportsCommonService.setSelectedReport(reportData);
    } catch (error) {
      this.loadingSpinnerService.updateMessage('');
      this.loadingSpinnerService.hide(CONSTANTS.DASHBOARD_SPINNER);
      console.error('GraphicsReportsService: Error generating report:', error);
      throw error;
    }
  }

  private async generateTableData(reportId: ReportList): Promise<ITableData[]> {
    let tableData: ITableData[] = [];
    const preferenceData = this.commonDataService.getPreferenceData()?.preferences;
    if(preferenceData) {
      this.showColourSymbol = preferenceData?.showColorReport ?? false;
      this.showIO = preferenceData?.showIO ?? false;
    }
    switch (reportId) {
      case ReportList.ApplicationDetailViewModel:
        // TODO: Implement system details report
        // const systemDetailsData = await this.generateAllSystemDetailsReportData();
        tableData = []
        break;
      case ReportList.UsedSymbolViewModel:
        // Return cached data if already processed
        if (this.cartoonReportData.length > 0 && this.lastRenderedWithColourSymbol === this.showColourSymbol) {
          tableData = this.cartoonReportData;
        } else {
          tableData = await this.getCartoonFile();
        }
        break;
      default:
        tableData = [];
        break;
    }
    return tableData;
  }

  private async getCartoonFile(): Promise<ITableData[]> {
    try {
      if(this.cartoonsData.length === 0 )
      {
        this.cartoonsIdList = [];
        const reportTable = await this.generateDrawingSymbolReportData();
        if(reportTable.length === 0) {
          return [];
        }
        this.cartoonsData = await firstValueFrom(this.reportsAPIService.getCartoonFiles(this.cartoonsIdList));
        this.cartoonReportData = reportTable;
      }
      this.cartoonReportData[0].data =await this.collectAndProcessDrawingSymbols();
      this.lastRenderedWithColourSymbol = this.showColourSymbol;
      return this.cartoonReportData;
    } catch (error) {
      console.error('Error fetching cartoon files:', error);
      throw error;
    }
  }

  private async generateDrawingSymbolReportData(): Promise<ITableData[]> {
    let sqWebProject = this.commonDataService.getSQWebProject();
    if (sqWebProject.isItemVisible) {
      const materialDataCollection = await this.indexDBService.getAllMaterialData();
      if (materialDataCollection) {
        for (const area of sqWebProject.areas) {
          if (area.isItemVisible) {
            this.processArea(area, materialDataCollection);
          }
        }
        return [
          {
            title: this.getTableHeader(),
            data: [],
            tableFooter: [],
            isPageBreakAfter: false,
            isTableBordered: false
          }
        ];
      }
    }
    return [];
  }

  private processArea(area: SQWebArea, materialDataCollection: MaterialLevelwiseData) {
    for (const child of area.children) {
      const childItem = child as SQWebAreaChildren;
      if (childItem.nodeType === CONSTANTS.NODETYPE_AREA) {
        const childArea = childItem as SQWebArea;
        if (childArea.isItemVisible) {
          this.processArea(childArea, materialDataCollection);
        }
      } else if (childItem.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM) {
        const system = childItem as SQWebSystem;
        if (system.isItemVisible) {
          const systemData = materialDataCollection.materialsCollection?.[system.itemUniqueId];
          if (systemData?.systemChildrenCollection?.length > 0) {
            this.processSystemChildren(systemData.systemChildrenCollection);
          }
        }
      }
    }
  }

  private processSystemChildren(systemChildren: SystemChildren) {
    for (const item of systemChildren) {
      const material = item as Material;
      const cartoonId = material.cartoon.trim();
      if (!cartoonId || this.cartoonsIdList.includes(cartoonId)) {
        continue;
      }
      this.cartoonsIdList.push(cartoonId);
    }
  }

  // Empty table headers for the drawing symbols report.
  private getTableHeader(): IDataItem[] {
    return [
      { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR },
      { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR },
      { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR },
      { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR },
      { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR }
    ];
  }

  private async collectAndProcessDrawingSymbols(): Promise<IData[]> {
    const processedSymbols: IDataItem[] = [];
    const cartoonFileNames: IDataItem[] = [];
    const sortedCartoonsData = [... this.cartoonsData].sort((a, b) => a.name.localeCompare(b.name));
    for (let symbol of sortedCartoonsData) {
      let svg = this.showColourSymbol ? symbol.cartoonColorSvg : symbol.cartoonSvg;
      if (!svg || svg.trim().length === 0) {
        // Skip symbols with empty SVG data
        processedSymbols.push(this.createImageDataItem('', false));
        cartoonFileNames.push(this.createImageDataItem(symbol.name, false));
        continue;
      }
      try {
        const convertedPng = await this.convertSvgToPng(svg);
        processedSymbols.push(this.createImageDataItem(convertedPng, true));
      } catch (error) {
        console.error('Error converting SVG to PNG:', error);
        // Push a non-image data item in case of error to maintain table structure
        processedSymbols.push(this.createImageDataItem('', false));
      }
      cartoonFileNames.push(this.createImageDataItem(symbol.name, false));
    }
    return this.getCartoonsRowWiseData(processedSymbols, cartoonFileNames, CONSTANTS.FIVE);
  }

  private createImageDataItem(data: string, renderAsImage: boolean): IDataItem {
    return {
      data,
      columnspan: CONSTANTS.ONE,
      hierarchyLevel: CONSTANTS.ZERO,
      alignment: CONSTANTS.LEFT_ALIGNMENT,
      renderAsImage: renderAsImage
    };
  }

  private getCartoonsRowWiseData(cartoons: IDataItem[], cartoonFileNames: IDataItem[], itemsPerRow: number = CONSTANTS.FIVE): IData[] {
    const result: IData[] = [];
    for (let i = 0; i < cartoons.length; i += itemsPerRow) {
      const rowItems = cartoons.slice(i, i + itemsPerRow);
      const nameItems = cartoonFileNames.slice(i, i + itemsPerRow);
      result.push({ data: rowItems }, { data: nameItems });
    }
    return result;
  }

  private convertSvgToPng(svgBase64: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas 2D context'));
            return;
          }
          canvas.width = CONSTANTS.CANVAS_WIDTH;
          canvas.height = CONSTANTS.CANVAS_HEIGHT;
          // Clear canvas
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          // Set white background
          ctx.fillStyle = CONSTANTS.REPORT_WHITE_COLOR;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          // Draw SVG image onto canvas (now that it's loaded)
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          // Convert canvas to PNG base64
          const pngDataUrl = canvas.toDataURL('image/png');
          // Clean up canvas reference
          canvas.width = 0;
          canvas.height = 0;
          resolve(pngDataUrl); // Return full data URL, not just base64 part
        } catch (error) {
          reject(error);
        }
      };
      img.onerror = () => {
        reject(new Error('Failed to load SVG image'));
      };
      // Set src after setting up event handlers
      img.src = 'data:image/svg+xml;base64,' + svgBase64;
    });
  }

  ngOnDestroy(): void {
    this.resetCartoonData();
  }
  
  private resetCartoonData() {
    this.cartoonsIdList = [];
    this.cartoonsData = [];
    this.cartoonReportData = [];
    this.lastRenderedWithColourSymbol = false;
  }
  
}
